import {
  Alert,
  Button,
  Card,
  Result,
  Space,
  Spin,
  Popconfirm,
  Typography,
  message
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  acceptInvitation,
  declineInvitation,
  getInvitation,
  requestAccountSetupEmail,
  type InvitationDetails
} from '../../service/invitation.service';
import { getOidcManager } from '../../service/auth/oidc';
import { fetchUser } from '../../redux/auth/actions';
import {
  grantLine,
  invitationIssuer
} from '../../utils/invitations';
import {
  PLATFORM_ACCESS_PENDING_MESSAGE,
  refreshAfterPlatformGrant
} from '../../utils/platformAccess';

/**
 * Where an invitation link lands (`/invitations/<token>`).
 *
 * The invitee may have no account yet, so the details are public. Until there is a session the
 * page offers what the server says fits (`account_setup`): create an account, set the password
 * of the one provisioned for them (registration is closed, so Keycloak's mailed link is the
 * only way in), or "Sign in to accept". Accepting and declining both need one, and
 * the server requires the signed-in address to match the invitation's. Declining asks for
 * confirmation on the page itself.
 */
export default function AccessInvitation() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [invitation, setInvitation] = useState<InvitationDetails>();
  // The link itself is unusable (unknown, used, lapsed): nothing else to show.
  const [loadError, setLoadError] = useState('');
  // An action failed (wrong account, unverified email): the invitation stays on screen.
  const [error, setError] = useState('');
  const [acting, setActing] = useState<'accept' | 'decline' | null>(null);
  const [declined, setDeclined] = useState(false);
  const [resending, setResending] = useState(false);
  const signedIn = Boolean(JSON.parse(sessionStorage.getItem('auth') || '{}')?.current?.access_token);

  useEffect(() => {
    getInvitation(token).then(setInvitation).catch((err) => {
      setLoadError(err?.response?.data?.detail || 'This invitation is unavailable.');
    });
  }, [token]);

  const redirectToIdp = (extra?: { prompt?: string; login_hint?: string }) => {
    // Keep the opaque invitation path through the Keycloak round trip. Going
    // through `/login` relied on a separate localStorage handoff that can be
    // replaced by normal unauthenticated navigation.
    sessionStorage.setItem('oidc:returnTo', `/invitations/${token}`);
    void getOidcManager().signinRedirect(extra).catch(() => {
      setError('Unable to reach the identity provider. Please try again.');
    });
  };

  // With registration open the invitee would otherwise have to spot Keycloak's small
  // "Register" link; `prompt=create` (Keycloak 26) opens the registration form directly,
  // with the invited address filled in, and the round trip returns here to accept.
  const signIn = () => redirectToIdp({ login_hint: invitation?.email });
  const createAccount = () => redirectToIdp({ 
    prompt: 'create', 
    login_hint: invitation?.email 
  });

  // The set-up email Keycloak sent at issue may have lapsed or been missed; the invitee has
  // no session, so the token authorises the re-send.
  const resendSetupEmail = async () => {
    setResending(true);
    try {
      await requestAccountSetupEmail(token);
      message.success('Set-up email sent.');
    } catch (err: any) {
      message.error(err?.response?.status === 429
        ? 'Please wait before asking again.'
        : err?.response?.data?.detail || 'Could not send the set-up email.');
    } finally {
      setResending(false);
    }
  };

  const accept = async () => {
    setError('');
    setActing('accept');
    try {
      const accepted = await acceptInvitation(token);
      // Acceptance adds the membership (or the platform role) on the server. Refresh the
      // session-backed workspace, capability and platform-role state before entering the
      // protected application.
      if (accepted?.platform_role || invitation?.platform_role) {
        // The platform role lives in Keycloak and is resolved per access token, so the
        // token held here is renewed first; only a profile that now reports the role
        // may be sent to the console, or it would answer 403.
        const ready = await refreshAfterPlatformGrant(dispatch);
        message.success('Invitation accepted.');
        if (ready) {
          navigate('/admin', { replace: true });
        } else {
          message.info({
            content: PLATFORM_ACCESS_PENDING_MESSAGE,
            duration: 8
          });
          navigate('/workspaces', { replace: true });
        }
        return;
      }
      await dispatch<any>(fetchUser());
      message.success('Invitation accepted.');
      navigate('/workspaces', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not accept this invitation.');
    } finally {
      setActing(null);
    }
  };

  const decline = async () => {
    setError('');
    setActing('decline');
    try {
      await declineInvitation(token);
      message.success('Invitation declined.');
      setDeclined(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not decline this invitation.');
    } finally {
      setActing(null);
    }
  };

  return <Card style={{ maxWidth: 560, margin: '48px auto' }}>
    <Typography.Title
      level={2}
    >You’ve been invited to SeeWard</Typography.Title>
    {declined ? <Result
      status="info"
      title="Invitation declined"
      subTitle="No access was granted. You can close this page."
      extra={signedIn ? <Button
        type="primary"
        onClick={() => navigate('/workspaces', { replace: true })}
      >Go to my workspaces</Button> : undefined}
    /> : loadError ? <Alert
      type="error"
      message={loadError}
      showIcon /
    > : !invitation ? <Spin /> : <>
      {error && <Alert
        type="error"
        message={error}
        showIcon
        style={{ marginBottom: 16 }}
      />}
      <Typography.Paragraph>
Invitation for <strong>{invitation.email}</strong>
      </Typography.Paragraph>
      <Typography.Paragraph>
From <strong>{invitationIssuer(invitation)}</strong>
      </Typography.Paragraph>
      {invitation.platform_role && <Typography.Paragraph>
Platform access: <strong>Support admin</strong> (read-only)
      </Typography.Paragraph>}
      {invitation.grants?.length > 0 && <Typography.Paragraph>
        Access: {invitation.grants.map(grantLine).join(', ')}
      </Typography.Paragraph>}
      {signedIn ? <Space>
        <Button
          type="primary"
          loading={acting === 'accept'}
          disabled={acting !== null}
          onClick={accept}
        >Accept invitation</Button>
        <Popconfirm
          title="Decline this invitation?"
          description="No access will be granted. The organisation would have to invite you again."
          okText="Decline"
          okButtonProps={{ danger: true }}
          onConfirm={decline}
        >
          <Button
            loading={acting === 'decline'}
            disabled={acting !== null}
          >Decline</Button>
        </Popconfirm>
      </Space> : invitation.account_setup === 'register' ? <Space 
        direction="vertical"
      >
        <Button
          type="primary"
          onClick={createAccount}
        >Create your account</Button>
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={signIn}
        >I already have an account</Button>
      </Space> : invitation.account_setup === 'temporary_password' ? <Space
        direction="vertical"
      >
        <Alert
          type="info"
          showIcon
          message="Sign in with the temporary password from your invitation email. You'll choose your own password before you continue."
        />
        <Button
          type="primary"
          onClick={signIn}
        >Sign in to accept</Button>
      </Space> : invitation.account_setup === 'set_password' ? <Space
        direction="vertical"
      >
        <Alert
          type="info"
          showIcon
          message={`We've emailed ${invitation.email} a link to set your password. It brings you back here to accept.`}
        />
        <Button
          loading={resending}
          onClick={resendSetupEmail}
        >Resend the set-up email</Button>
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={signIn}
        >Already set your password? Sign in</Button>
      </Space> : <Button
        type="primary"
        onClick={signIn}
      >Sign in to accept</Button>}
    </>}
  </Card>;
}
